import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SliderInner from './SliderInner';
import SliderArrow from '../buttons/SliderArrow';

const cookieKey = 'show_search_slider';

class Slider extends Component {
  // eslint-disable-next-line no-useless-escape
  cookieRegex = new RegExp(`(?:(?:^|.*;\s*)${cookieKey}\s*\=\s*([^;]*).*$)|^.*$`);

  //
  state = {
    hidden: (document.cookie.replace(this.cookieRegex, '$1') === '1'),
    firstSlideIndex: null,
    visibleSlideCount: 0,
    step: 6,
    firstPageLoaded: false,
    lastPageLoaded: false,
    // To know, from where last page got loaded:
    // - 'initial' -> when initially opening user profile and loading current page
    // - 'update' -> 'loadMoreCallback' called from 'componentDidUpdate'
    // - 'next' -> 'loadMoreCallback' called from 'nextSlide'
    lastPageFrom: 'initial',
  };

  //
  sliderRef = React.createRef();

  //
  sliderArrowLeftRef = React.createRef();

  //
  sliderArrowRightRef = React.createRef();

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      firstPageLoaded,
      lastPageLoaded,
      lastPageFrom,
      visibleSlideCount,
    } = this.state;
    let { firstSlideIndex } = this.state;

    const {
      activeSlideIndex,
      loading,
      items,
      loadMoreCallback,
      loadPreviousCallback,
    } = this.props;

    // Initial update, first slide index here not set yet, so set it, if 'activeSlideIndex' is given
    // (and do nothing else yet)
    if (firstSlideIndex === null) {
      if (activeSlideIndex !== null) {
        this.setFirstSlideIndex(activeSlideIndex - 1);
      }
      return;
    }

    // After setting first slide index, call 'handleResize',
    // to update 'visibleSlideCount' and 'step'
    // (and don't do anything else yet, because after update,
    // this function will be called again and then this case will be skipped)
    if (prevState.firstSlideIndex === null) {
      this.handleResize();
      return;
    }

    // From here, if something is already loading, don't do anything else
    if (loading) {
      return;
    }

    // If 'lastPageLoaded' is not yet set to true,
    // and items length is not divisible by 20 (not full page)
    // or current items length is smaller than previous
    // (can be if 20 "temporary members" added for loading,
    // but nothing found for next page, so they got removed when finished loading,
    // but items length is still divisible by 20),
    // call 'handleLastPage'.
    // That will take care of 'firstSlideIndex',
    // and also try to load additional previous page,
    // if necessary (if 'lastPageFrom' will be set to 'initial').
    // ('handleLastPage' also will set 'lastPageLoaded' to true,
    // so next time this case will be skipped)
    if (!lastPageLoaded
      && (items.length % 20 !== 0 || items.length < prevProps.items.length)
    ) {
      this.handleLastPage();
      return;
    }

    // If here slider is full (loaded enough users to fill it), no need to do anything else
    if ((firstSlideIndex + visibleSlideCount) <= items.length) {
      return;
    }

    // If slider is not full and 'lastPageLoaded' is not yet set to true,
    // try to load more users (next page).
    if (!lastPageLoaded) {
      if (lastPageFrom !== 'update') {
        this.setLastPageFrom('update');
      }
      loadMoreCallback();
      return;
    }

    // If slider is not full, but 'lastPageLoaded' already set to true,
    // and 'firstPageLoaded' also already set to true..
    // Can't do nothing more, there just aren't enough users to fill all slider.
    // All results already visible.
    if (firstPageLoaded) {
      return;
    }

    // This is case when:
    // - slider is not full
    // - 'lastPageLoaded' already set to true
    // - 'firstPageLoaded' not yet set to true
    // So in this case, try to fill slider by loading previous page
    const setNewFirstSlideIndex = (first) => {
      if (first || visibleSlideCount > items.length + 20) {
        firstSlideIndex = 0;
      } else {
        firstSlideIndex = ((items.length + 20) - visibleSlideCount) - 1;
      }
      this.setState({
        firstSlideIndex,
        firstPageLoaded: first,
      });
    };
    loadPreviousCallback(setNewFirstSlideIndex);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  setFirstSlideIndex = (index) => {
    const firstSlideIndex = index < 0 ? 0 : index;
    this.setState({
      firstSlideIndex,
    });
  };

  setLastPageFrom = (lastPageFrom) => {
    this.setState({
      lastPageFrom,
    });
  };

  handleLastPage = () => {
    const { lastPageFrom, visibleSlideCount, step } = this.state;
    let { firstSlideIndex } = this.state;
    const { items } = this.props;

    // Next
    if (lastPageFrom === 'next') {
      if (items.length > visibleSlideCount) {
        firstSlideIndex = (items.length - visibleSlideCount) - 1;
      }
    }

    // Update
    if (lastPageFrom === 'update') {
      const currentlyVisible = items.length - firstSlideIndex;
      if (currentlyVisible <= visibleSlideCount && items.length > visibleSlideCount) {
        firstSlideIndex = (items.length - visibleSlideCount) - 1;
      }
    }

    // Initial
    if (lastPageFrom === 'initial') {
      const setNewFirstSlideIndex = (first) => {
        if (first || step > 20) {
          firstSlideIndex = 0;
        } else {
          firstSlideIndex = ((items.length + 20) - visibleSlideCount) - 1;
        }

        this.setState({
          firstSlideIndex,
          firstPageLoaded: first,
        });
      };
      this.props.loadPreviousCallback(setNewFirstSlideIndex);
    }

    this.setState({
      firstSlideIndex,
      lastPageLoaded: true,
    });
  };

  handleResize = () => {
    if (this.state.hidden) {
      return;
    }
    if (typeof this.sliderRef.current === 'undefined' || this.sliderRef.current === null) {
      return;
    }
    const specs = this.sliderRef.current.getBoundingClientRect();
    let offset = 0;
    const arrows = [
      this.sliderArrowLeftRef.current,
      this.sliderArrowRightRef.current,
    ];
    arrows.forEach((arrow) => {
      if (typeof arrow !== 'undefined' && arrow !== null) {
        const rect = arrow.getBoundingClientRect();
        offset += rect.width;
      }
    });
    const visibleSlideCount = Math.round((specs.width - offset) / this.props.slideStyle.width) - 1;
    const state = {
      visibleSlideCount,
    };
    if (this.props.step) {
      state.step = this.props.step;
    } else {
      state.step = visibleSlideCount;
    }
    this.setState(state);
  };

  prevSlide = () => {
    if (this.props.loading) {
      return;
    }
    let newFirstSlideIndex = this.state.firstSlideIndex - this.state.step;
    if (newFirstSlideIndex > -1) {
      this.setFirstSlideIndex(newFirstSlideIndex);
      return;
    }

    const setNewFirstSlideIndex = (first) => {
      if (first || this.state.step > 20) {
        newFirstSlideIndex = 0;
      } else {
        newFirstSlideIndex = 20 + newFirstSlideIndex;
      }

      this.setState({
        firstSlideIndex: newFirstSlideIndex,
        firstPageLoaded: first,
      });
    };

    this.props.loadPreviousCallback(setNewFirstSlideIndex);
  };

  nextSlide = () => {
    if (this.props.items.length > this.props.maxItems) {
      // Can't use 'window.location.reload()', because then slider disappears.
      // Can't use simply 'window.location.href = window.location.href',
      // because it doesn't reload the page if the current url contains a '#''.
      // (current url contains '#', after navigating in slider)
      // eslint-disable-next-line
      window.location.href = window.location.href.split('#')[0];
    }

    if (this.props.loading) {
      return;
    }

    const total = this.props.items.length;
    const newFirstSlideIndex = this.state.firstSlideIndex + this.state.step;

    if (this.state.visibleSlideCount < (total - newFirstSlideIndex)) {
      this.setFirstSlideIndex(newFirstSlideIndex);
      return;
    }

    if (this.state.lastPageLoaded) {
      this.setFirstSlideIndex((this.props.items.length - this.state.visibleSlideCount) - 1);
      return;
    }

    if (this.state.lastPageFrom !== 'next') {
      this.setLastPageFrom('next');
    }
    this.props.loadMoreCallback(this.setFirstSlideIndex(newFirstSlideIndex));
  };

  hide = () => {
    document.cookie = `${cookieKey}=1`;
    this.setState({
      hidden: true,
    });
  };

  show = () => {
    document.cookie = `${cookieKey}=0`;
    this.setState({
      hidden: false,
    });
    this.handleResize();
  };

  render() {
    const {
      hidden,
      visibleSlideCount,
      firstSlideIndex,
    } = this.state;

    const {
      title,
      items,
      size,
      activeSlideIndex,
      slideStyle,
      offset,
      setCurrentMemberId,
    } = this.props;

    if (hidden) {
      return (
        <div className="slider-container is-hidden">
          <div
            role="button"
            className="slider-hideButton slider-hideButton--wtext"
            onClick={this.show}
            onKeyDown={this.show}
            style={{ bottom: 0, top: 'initial' }}
            tabIndex="0"
          >
            <SliderArrow type="up" style={{ left: 0 }} />
            <span className="prs pls">{title}</span>
            <SliderArrow type="up" style={{ right: 0 }} />
          </div>
        </div>
      );
    }

    if (visibleSlideCount === 0) {
      return (
        <div className="slider-container">
          <div ref={this.sliderRef} className="slider" />
        </div>
      );
    }

    if (items.length < 1) {
      return (
        <div />
      );
    }

    let className = 'slider';
    if (size) {
      className += `  slider--${size}`;
    }

    return (
      <div className="slider-container">
        <div
          role="button"
          className="slider-hideButton slider-hideButton--wtext"
          onClick={this.hide}
          onKeyDown={this.hide}
          tabIndex="0"
        >
          <SliderArrow type="down" style={{ left: 0 }} />
          <span className="prs pls">{title}</span>
          <SliderArrow type="down" style={{ right: 0 }} />
        </div>
        <div
          className={className}
          ref={this.sliderRef}
        >
          <SliderArrow
            type="left"
            ref={this.sliderArrowLeftRef}
            className="slider-button"
            onClick={this.prevSlide}
            style={{ left: 0 }}
          />
          <SliderInner
            activeSlideIndex={activeSlideIndex}
            slideStyle={slideStyle}
            offset={offset}
            items={items}
            firstSlideIndex={firstSlideIndex}
            setCurrentMemberId={setCurrentMemberId}
          />
          <SliderArrow
            type="right"
            ref={this.sliderArrowRightRef}
            className="slider-button"
            onClick={this.nextSlide}
            style={{ right: 0 }}
          />
        </div>
      </div>
    );
  }
}

Slider.propTypes = {
  maxItems: PropTypes.number,
  slideStyle: PropTypes.instanceOf(Object),
  items: PropTypes.instanceOf(Array),
  offset: PropTypes.number,
  activeSlideIndex: PropTypes.number,
  title: PropTypes.string,
  size: PropTypes.number,
  loadMoreCallback: PropTypes.func,
  loadPreviousCallback: PropTypes.func,
  loading: PropTypes.bool,
  step: PropTypes.number,
  setCurrentMemberId: PropTypes.func.isRequired,
};

Slider.defaultProps = {
  maxItems: 600, // 30 pages
  slideStyle: {
    width: 80,
    height: 80,
  },
  items: [],
  offset: 20,
  activeSlideIndex: null,
  title: '',
  size: null,
  loadMoreCallback: null,
  loadPreviousCallback: null,
  loading: false,
  step: null,
};

export default Slider;
